import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';

import { Locale, messages } from '../messages';
import { supabase, useSupabase } from '../supabase';
import { DEFAULT_LOCALE, detectBrowserLocale, getStoredLocale, isLocale, setStoredLocale } from './locale';

type LocaleContextValue = {
	locale: Locale;
	setLocale: (locale: Locale) => void;
};

const localeContext = createContext<LocaleContextValue>({
	locale: DEFAULT_LOCALE,
	setLocale: () => {},
});

export const useLocale = () => useContext(localeContext);

export const LocaleProvider = ({ children }: { children?: ReactNode }) => {
	const { session } = useSupabase();

	const userLocale = session?.user?.user_metadata?.locale;

	// Used while logged out: persisted choice, otherwise the browser language.
	const [anonymousLocale, setAnonymousLocale] = useState<Locale>(() => getStoredLocale() ?? detectBrowserLocale());

	const locale: Locale = isLocale(userLocale) ? userLocale : anonymousLocale;

	useEffect(() => {
		document.documentElement.lang = locale;
	}, [locale]);

	const setLocale = useCallback(
		(next: Locale) => {
			setStoredLocale(next);
			setAnonymousLocale(next);

			if (session) {
				supabase.auth.updateUser({ data: { locale: next } });
			}
		},
		[session],
	);

	const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

	return (
		<localeContext.Provider value={value}>
			<IntlProvider locale={locale} defaultLocale={DEFAULT_LOCALE} messages={messages[locale]}>
				{children}
			</IntlProvider>
		</localeContext.Provider>
	);
};
